import React, { lazy, Suspense, useCallback, useMemo } from "react";
import { useNavigation } from "./hooks/useNavigation";
import { AuthProvider, useAuthContext } from "./contexts/AuthContext";
import { AppLayout } from "./layouts/AppLayout";
import { HomePage } from "./pages/HomePage";
import { Loader } from "./ui/components/Loader";

// Lazy load pages with better error handling
const StudyPage = lazy(() =>
  import("./pages/StudyPage")
    .then((module) => ({ default: module.StudyPage }))
    .catch((err) => {
      console.error("Failed to load StudyPage:", err);
      return { default: () => <div>Failed to load Study Page</div> };
    })
);

const DashboardPage = lazy(() =>
  import("./pages/DashboardPage")
    .then((module) => ({ default: module.DashboardPage }))
    .catch((err) => {
      console.error("Failed to load DashboardPage:", err);
      return { default: () => <div>Failed to load Dashboard</div> };
    })
);

const StudyModulesPage = lazy(() =>
  import("./pages/StudyModulesPage")
    .then((module) => ({ default: module.StudyModulesPage }))
    .catch((err) => {
      console.error("Failed to load StudyModulesPage:", err);
      return { default: () => <div>Failed to load Study Modules</div> };
    })
);

const FlashcardsPage = lazy(() =>
  import("./pages/FlashcardsPage")
    .then((module) => ({ default: module.FlashcardsPage }))
    .catch((err) => {
      console.error("Failed to load FlashcardsPage:", err);
      return { default: () => <div>Failed to load Flashcards</div> };
    })
);

const QuizzesPage = lazy(() =>
  import("./pages/QuizzesPage")
    .then((module) => ({ default: module.QuizzesPage }))
    .catch((err) => {
      console.error("Failed to load QuizzesPage:", err);
      return { default: () => <div>Failed to load Quizzes</div> };
    })
);

const AudioLessonsPage = lazy(() =>
  import("./pages/AudioLessonsPage")
    .then((module) => ({ default: module.AudioLessonsPage }))
    .catch((err) => {
      console.error("Failed to load AudioLessonsPage:", err);
      return { default: () => <div>Failed to load Audio Lessons</div> };
    })
);

const KnowledgeTrackerPage = lazy(() =>
  import("./pages/KnowledgeTrackerPage")
    .then((module) => ({ default: module.KnowledgeTrackerPage }))
    .catch((err) => {
      console.error("Failed to load KnowledgeTrackerPage:", err);
      return { default: () => <div>Failed to load Knowledge Tracker</div> };
    })
);

const AuthModal = lazy(() =>
  import("./components/AuthModal")
    .then((module) => ({ default: module.AuthModal }))
    .catch((err) => {
      console.error("Failed to load AuthModal:", err);
      return { default: () => null };
    })
);

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: {
    children: React.ReactNode;
    fallback?: React.ReactNode;
  }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex h-screen w-full items-center justify-center bg-default-background">
            <div className="flex flex-col items-center gap-4">
              <div className="text-lg font-semibold text-red-600">
                Something went wrong
              </div>
              <button
                onClick={() => this.setState({ hasError: false })}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Try again
              </button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

// Optimized loading component
const PageLoader = React.memo(() => (
  <div className="flex h-full w-full items-center justify-center bg-default-background">
    <div className="flex flex-col items-center gap-4">
      <Loader size="large" />
      <span className="text-body font-body text-subtext-color">
        Loading page...
      </span>
    </div>
  </div>
));

function AppContent() {
  const { currentPage, navigate } = useNavigation("home");
  const { user, loading } = useAuthContext();
  const [showAuthModal, setShowAuthModal] = React.useState(false);

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  // Memoize the navigation handler to prevent unnecessary re-renders
  const handleNavigate = useCallback(
    (page: typeof currentPage) => {
      // If user is not authenticated and trying to access protected pages
      if (!user && !loading && page !== "home") {
        setShowAuthModal(true);
        return;
      }
      navigate(page);
    },
    [user, navigate]
  );

  // Memoize the fallback component
  const fallback = useMemo(
    () => (
      <div className="flex h-full w-full items-center justify-center bg-default-background">
        <div className="flex flex-col items-center gap-4">
          <Loader size="large" />
          <span className="text-body font-body text-subtext-color">
            Loading page...
          </span>
        </div>
      </div>
    ),
    []
  );

  // Memoize the page rendering function
  const renderCurrentPage = useCallback(() => {
    switch (currentPage) {
      case "home":
        return (
          <HomePage currentPage={currentPage} onNavigate={handleNavigate} />
        );
      case "study":
        return (
          <ErrorBoundary>
            <Suspense fallback={fallback}>
              <StudyPage
                currentPage={currentPage}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </ErrorBoundary>
        );
      case "dashboard":
        return (
          <ErrorBoundary>
            <Suspense fallback={fallback}>
              <DashboardPage
                currentPage={currentPage}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </ErrorBoundary>
        );
      case "study-modules":
        return (
          <ErrorBoundary>
            <Suspense fallback={fallback}>
              <StudyModulesPage
                currentPage={currentPage}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </ErrorBoundary>
        );
      case "flashcards":
        return (
          <ErrorBoundary>
            <Suspense fallback={fallback}>
              <FlashcardsPage />
            </Suspense>
          </ErrorBoundary>
        );
      case "quizzes":
        return (
          <ErrorBoundary>
            <Suspense fallback={fallback}>
              <QuizzesPage />
            </Suspense>
          </ErrorBoundary>
        );
      case "audio-lessons":
        return (
          <ErrorBoundary>
            <Suspense fallback={fallback}>
              <AudioLessonsPage />
            </Suspense>
          </ErrorBoundary>
        );
      case "knowledge-tracker":
        return (
          <ErrorBoundary>
            <Suspense fallback={fallback}>
              <KnowledgeTrackerPage
                currentPage={currentPage}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </ErrorBoundary>
        );
      default:
        return (
          <HomePage currentPage={currentPage} onNavigate={handleNavigate} />
        );
    }
  }, [currentPage, handleNavigate, fallback]);

  // Show auth modal if user is not authenticated and not on home page
  React.useEffect(() => {
    if (!user && !loading && currentPage !== "home") {
      setShowAuthModal(true);
    }
  }, [user, loading, currentPage]);

  // NOW we can have early returns after all hooks are called
  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-default-background">
        <div className="flex flex-col items-center gap-4">
          <Loader size="large" />
          <span className="text-body font-body text-subtext-color">
            Loading MasteryMint...
          </span>
        </div>
      </div>
    );
  }

  // Homepage uses DefaultPageLayout directly, other pages use AppLayout
  if (currentPage === "home") {
    return (
      <>
        {renderCurrentPage()}
        <ErrorBoundary>
          <Suspense fallback={null}>
            <AuthModal
              open={showAuthModal}
              onOpenChange={(open) => {
                setShowAuthModal(open);
                if (!open && !user) {
                  navigate("home");
                }
              }}
            />
          </Suspense>
        </ErrorBoundary>
      </>
    );
  }

  return (
    <>
      <AppLayout currentPage={currentPage} onNavigate={handleNavigate}>
        {renderCurrentPage()}
      </AppLayout>
      <ErrorBoundary>
        <Suspense fallback={null}>
          <AuthModal
            open={showAuthModal}
            onOpenChange={(open) => {
              setShowAuthModal(open);
              if (!open && !user) {
                navigate("home");
              }
            }}
          />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
